import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emojis } from 'app/constants';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  getShowAuthentication,
  getShowStarterScreen,
  setShowAuthentication,
  setShowBackButton,
} from 'app/slices/AppUiSlice';
import { getSelectedGame, getSelectedTeamID, getSelectedWeek, getSelectedYear } from 'app/slices/GamesSlice';
import { getSelectedScene, setSelectedScene, setShowAuthenticationScene } from 'app/slices/NavigationSlice';
import { getIsUserLoggedIn, getUserAuthState, UserAuthState } from 'app/slices/UserSlice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppScene, Scene } from 'types';

const CONSOLE_ON = getConsoleState(ConsoleID.UseSelectedScene);

const useSelectedScene = () => {
  const localSelectedSceneRef = useRef<Scene | null>(null);
  const localUserAuthStateRef = useRef<UserAuthState>(UserAuthState.Init);
  const localIsStarterScreenOpenRef = useRef<boolean>(true);

  const dispatch = useAppDispatch();

  const selectedScene = useAppSelector(getSelectedScene);
  const selectedGameID = useAppSelector(getSelectedGame);
  const selectedTeamID = useAppSelector(getSelectedTeamID);
  const selectedYear = useAppSelector(getSelectedYear);
  const selectedWeek = useAppSelector(getSelectedWeek);
  const isShowingStarterScreen = useAppSelector(getShowStarterScreen);
  const showAuthentication = useAppSelector(getShowAuthentication);

  const userAuthState = useAppSelector(getUserAuthState);
  const isLoggedIn = useAppSelector(getIsUserLoggedIn);

  const [standingsActive, setStandingsActive] = useState(false);
  const [showStatsForAllTeams, setShowStatsForAllTeams] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [showWeeklyGames, setShowWeeklyGames] = useState(false);
  const [showCategoryRankings, setShowCategoryRankings] = useState(false);

  // const navigate = useNavigate();

  useEffect(() => {
    localIsStarterScreenOpenRef.current = isShowingStarterScreen;
  }, [isShowingStarterScreen]);

  useEffect(() => {
    const msg = `${Emojis.eyes} %c selectedYear %c has changed to %c ${selectedYear} %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Pale, '', Cls.Val, '');
  }, [selectedYear]);

  useEffect(() => {
    const msg = `${Emojis.eyes} %c selectedWeek %c has changed to %c ${selectedWeek} %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Pale, '', Cls.Val, '');
  }, [selectedWeek]);

  useEffect(() => {
    const gameMsg = !selectedGameID ? 'none' : selectedGameID;
    const msg = `${Emojis.eyes} %c selectedGameID %c has changed to %c ${gameMsg} %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Pale, '', Cls.Val, '');
  }, [selectedGameID]);

  useEffect(() => {
    const msg = `${Emojis.eyes} %c selectedScene %c has changed to %c ${selectedScene} %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Pale, '', Cls.Val, '');
    switch (selectedScene) {
      case AppScene.CategoryRankings:
      case AppScene.GameDetails:
      case AppScene.Standings:
      case AppScene.TeamDetails:
      case AppScene.Teams:
        dispatch(setShowBackButton(true));
        break;
      case AppScene.WeeklyGames:
      default:
        dispatch(setShowBackButton(false));
    }
  }, [selectedScene]);

  useEffect(() => {
    if (userAuthState === UserAuthState.LoggedIn) {
      dispatch(setShowAuthenticationScene(false));
      dispatch(setShowAuthentication(false));
    }
  }, [userAuthState]);

  useEffect(() => {
    const teamMsg = !selectedTeamID ? 'none' : selectedTeamID;
    const msg = `${Emojis.eyes} %c selectedTeamID %c has changed to %c ${teamMsg} %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Pale, '', Cls.Val, '');
  }, [selectedTeamID]);

  useEffect(() => {
    localUserAuthStateRef.current = userAuthState;

    if (
      userAuthState !== UserAuthState.LoggedIn &&
      ((!isLoggedIn && userAuthState !== UserAuthState.Authenticating) || showAuthentication)
    ) {
      // localSelectedSceneRef.current = AppScene.Authentication;
      // dispatch(setSelectedScene(AppScene.Authentication));
      // navigate(`${AppRoutes.Auth}/${AppRoutes.Login}`, { replace: true });
    }
  }, [isLoggedIn, showAuthentication, userAuthState, dispatch]);

  const loggedOutWithStartingScreenClosed = useMemo(() => {
    return !(
      isLoggedIn &&
      localUserAuthStateRef.current === UserAuthState.LoggedIn &&
      !localIsStarterScreenOpenRef.current
    );
  }, [isLoggedIn, localUserAuthStateRef, localIsStarterScreenOpenRef]);

  useEffect(() => {
    if (loggedOutWithStartingScreenClosed) return;

    if (showLandingPage) {
      dispatch(setSelectedScene(AppScene.Landing));
      localSelectedSceneRef.current = AppScene.Landing;
    }
  }, [loggedOutWithStartingScreenClosed, dispatch]);

  const shouldUpdateSelectedScene = useCallback(
    (scene: AppScene) => {
      const isSceneAlreadySelected = localSelectedSceneRef.current === scene;
      const userLoggedIn = localUserAuthStateRef.current === UserAuthState.LoggedIn;
      const starterScreenOpen = localIsStarterScreenOpenRef.current;

      return (
        (showStatsForAllTeams || showCategoryRankings) && !isSceneAlreadySelected && userLoggedIn && !starterScreenOpen
      );
    },
    [showStatsForAllTeams, showCategoryRankings],
  );

  useEffect(() => {
    if (shouldUpdateSelectedScene(AppScene.Standings)) {
      localSelectedSceneRef.current = AppScene.Standings;
      dispatch(setSelectedScene(AppScene.Standings));
    }
  }, [showStatsForAllTeams, dispatch]);

  useEffect(() => {
    if (shouldUpdateSelectedScene(AppScene.Teams)) {
      localSelectedSceneRef.current = AppScene.Teams;
      dispatch(setSelectedScene(AppScene.Teams));
    }
  }, [showStatsForAllTeams, dispatch]);

  useEffect(() => {
    if (shouldUpdateSelectedScene(AppScene.WeeklyGames)) {
      localSelectedSceneRef.current = AppScene.WeeklyGames;
      dispatch(setSelectedScene(AppScene.WeeklyGames));
    }
  }, [showWeeklyGames, dispatch]);

  useEffect(() => {
    if (shouldUpdateSelectedScene(AppScene.GameDetails) && selectedGameID?.length > 0) {
      localSelectedSceneRef.current = AppScene.GameDetails;
      dispatch(setSelectedScene(AppScene.GameDetails));
    }
  }, [selectedGameID, dispatch]);

  useEffect(() => {
    if (shouldUpdateSelectedScene(AppScene.TeamDetails) && selectedTeamID.length > 0) {
      localSelectedSceneRef.current = AppScene.TeamDetails;
      dispatch(setSelectedScene(AppScene.TeamDetails));
    }
  }, [selectedTeamID, dispatch]);

  useEffect(() => {
    if (shouldUpdateSelectedScene(AppScene.CategoryRankings)) {
      localSelectedSceneRef.current = AppScene.CategoryRankings;
      dispatch(setSelectedScene(AppScene.CategoryRankings));
    }
  }, [showCategoryRankings, dispatch]);

  return {
    selectedScene,
    standingsActive,
    setShowStatsForAllTeams,
    setShowCategoryRankings,
    setStandingsActive,
    setShowLandingPage,
    setShowAuthentication,
    setShowWeeklyGames,
  };
};

export default useSelectedScene;
